import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Global, app-wide keyboard shortcuts (slice 5).
//
// To wire: add `useGlobalShortcuts()` and `<KeyboardCheatSheet />` once
// somewhere inside the Router tree (top of App.tsx). The hook's
// listeners are gated on text-input focus so typing into an answer
// textarea or settings field doesn't fire navigation.
//
// Bindings:
//   g d       -> dashboard ("/")
//   g g       -> current product's /graph if on a product page,
//                else dashboard
//   ?         -> open the cheat sheet modal (consume via the
//                useCheatSheet store below + <KeyboardCheatSheet />)
//
// Per-surface bindings (not handled here; documented in the cheat sheet):
//   focused review : ←/→ navigate, ⌘↵ submit, esc back, Y/N decisions
//   graph          : esc deselects the open node panel
//
// Leader-key sequence: pressing `g` opens a 1.5s window where the next
// key resolves the binding. Pressing any key other than the registered
// next-keys cancels. Pressing `g` again restarts the window.

const LEADER_TIMEOUT_MS = 1500;

// Tiny pub/sub for the cheat-sheet modal so this hook can flip a flag
// the modal subscribes to without prop-drilling. Kept module-local — a
// global key viewer is fine in module scope and lighter than wiring a
// store.
type Listener = (open: boolean) => void;
let cheatSheetOpen = false;
const listeners = new Set<Listener>();

export function openCheatSheet() {
  cheatSheetOpen = true;
  for (const fn of listeners) fn(true);
}

export function closeCheatSheet() {
  cheatSheetOpen = false;
  for (const fn of listeners) fn(false);
}

export function useCheatSheetOpen(): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState(cheatSheetOpen);
  useEffect(() => {
    const fn: Listener = (v) => setOpen(v);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return [
    open,
    (v: boolean) => {
      if (v) openCheatSheet();
      else closeCheatSheet();
    },
  ];
}

function isTextInput(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "INPUT" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useGlobalShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let leader: "g" | null = null;
    let leaderTimer: number | null = null;

    const clearLeader = () => {
      leader = null;
      if (leaderTimer != null) {
        window.clearTimeout(leaderTimer);
        leaderTimer = null;
      }
    };

    const armLeader = (key: "g") => {
      leader = key;
      if (leaderTimer != null) window.clearTimeout(leaderTimer);
      leaderTimer = window.setTimeout(clearLeader, LEADER_TIMEOUT_MS);
    };

    const onKey = (e: KeyboardEvent) => {
      // Ignore key events while the user is typing into a field; only
      // allow `Escape` to bubble up (which is owned by the surface, not
      // by the global handler anyway).
      if (isTextInput(e.target)) return;

      // Modifier keys disable global shortcuts so browser/system bindings
      // (⌘K, ⌘R, …) don't get hijacked. ? on most US keyboards is
      // shift-/, which is fine — we explicitly allow shift below.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Cheat sheet on `?`. Use e.key so locale doesn't matter and we
      // don't have to special-case shift detection.
      if (e.key === "?") {
        e.preventDefault();
        clearLeader();
        openCheatSheet();
        return;
      }

      // Esc closes the cheat sheet if it's open. The Modal owns its own
      // Escape handler when it's the `KeyboardCheatSheet` we render, but
      // catching it here lets the user close + still cancel a leader.
      if (e.key === "Escape") {
        if (cheatSheetOpen) closeCheatSheet();
        clearLeader();
        return;
      }

      // Leader-key chord handling.
      if (leader === "g") {
        const k = e.key.toLowerCase();
        if (k === "d") {
          e.preventDefault();
          clearLeader();
          navigate("/");
          return;
        }
        if (k === "g") {
          e.preventDefault();
          clearLeader();
          // /products/<pid>/...  -> /products/<pid>/graph; otherwise "/"
          const m = location.pathname.match(/^\/products\/([^/]+)/);
          if (m) navigate(`/products/${m[1]}/graph`);
          else navigate("/");
          return;
        }
        // Unknown follow-up key: cancel the chord and let the key fall
        // through (no preventDefault).
        clearLeader();
        return;
      }

      if (e.key === "g") {
        // Don't preventDefault — `g` alone has no effect, and forwarding
        // it lets a stray press still type into a focused element if the
        // browser later decides one is. (We've already returned above on
        // text-input target.)
        armLeader("g");
        return;
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (leaderTimer != null) window.clearTimeout(leaderTimer);
    };
  }, [navigate, location.pathname]);
}
